/**
 * CMS SDK - API 客户端
 * 内容管理系统后端接口封装
 * 
 * 使用 BaseApiClient 基类简化代码
 */

import { BaseApiClient } from '@quantabit/sdk-config';

/**
 * CMS API 客户端
 */
export class CmsApiClient extends BaseApiClient {
  constructor(config = {}) {
    super('/cms', config);
  }

  // ============ 内容管理 ============

  /**
   * 获取内容列表
   * @param {Object} params - 查询参数
   */
  async getContents(params = {}) {
    return this.get('/contents', params);
  }

  /**
   * 获取内容详情
   * @param {string} contentId - 内容 ID
   */
  async getContent(contentId) {
    return this.get(`/contents/${contentId}`);
  }

  /**
   * 通过别名获取内容
   * @param {string} slug - 内容别名
   */
  async getContentBySlug(slug) {
    return this.get(`/contents/slug/${slug}`);
  }

  /**
   * 创建内容（管理员）
   * @param {Object} data - 内容数据
   */
  async createContent(data) {
    return this.post('/contents', data);
  }

  /**
   * 更新内容（管理员）
   * @param {string} contentId - 内容 ID
   * @param {Object} updates - 更新数据
   */
  async updateContent(contentId, updates) {
    return this.put(`/contents/${contentId}`, updates);
  }

  /**
   * 删除内容（管理员）
   * @param {string} contentId - 内容 ID
   */
  async deleteContent(contentId) {
    return this.delete(`/contents/${contentId}`);
  }

  /**
   * 发布内容（管理员）
   * @param {string} contentId - 内容 ID
   */
  async publishContent(contentId) {
    return this.post(`/contents/${contentId}/publish`);
  }

  /**
   * 下架内容（管理员）
   * @param {string} contentId - 内容 ID
   */
  async unpublishContent(contentId) {
    return this.post(`/contents/${contentId}/unpublish`);
  }

  // ============ 分类管理 ============

  /**
   * 获取分类列表
   * @param {Object} params - 查询参数
   */
  async getCategories(params = {}) {
    return this.get('/categories', params);
  }

  /**
   * 获取分类树
   */
  async getCategoryTree() {
    return this.get('/categories/tree');
  }

  /**
   * 创建分类（管理员）
   * @param {Object} data - 分类数据
   */
  async createCategory(data) {
    return this.post('/categories', data);
  }

  /**
   * 更新分类（管理员）
   * @param {string} categoryId - 分类 ID
   * @param {Object} updates - 更新数据
   */
  async updateCategory(categoryId, updates) {
    return this.put(`/categories/${categoryId}`, updates);
  }

  /**
   * 删除分类（管理员）
   * @param {string} categoryId - 分类 ID
   */
  async deleteCategory(categoryId) {
    return this.delete(`/categories/${categoryId}`);
  }

  // ============ 标签管理 ============

  /**
   * 获取标签列表
   * @param {Object} params - 查询参数
   */
  async getTags(params = {}) {
    return this.get('/tags', params);
  }

  /**
   * 获取热门标签
   * @param {number} limit - 返回数量
   */
  async getHotTags(limit = 20) {
    return this.get('/tags/hot', { limit });
  }

  /**
   * 创建标签（管理员）
   * @param {Object} data - 标签数据
   */
  async createTag(data) {
    return this.post('/tags', data);
  }

  // ============ 页面管理 ============

  /**
   * 获取页面列表
   * @param {Object} params - 查询参数
   */
  async getPages(params = {}) {
    return this.get('/pages', params);
  }

  /**
   * 获取页面详情
   * @param {string} pageId - 页面 ID
   */
  async getPage(pageId) {
    return this.get(`/pages/${pageId}`);
  }

  /**
   * 通过路径获取页面
   * @param {string} path - 页面路径
   */
  async getPageByPath(path) {
    return this.get('/pages/path', { path });
  }

  /**
   * 创建页面（管理员）
   * @param {Object} data - 页面数据
   */
  async createPage(data) {
    return this.post('/pages', data);
  }

  /**
   * 更新页面（管理员）
   * @param {string} pageId - 页面 ID
   * @param {Object} updates - 更新数据
   */
  async updatePage(pageId, updates) {
    return this.put(`/pages/${pageId}`, updates);
  }

  // ============ 模板管理 ============

  /**
   * 获取模板列表
   * @param {Object} params - 查询参数
   */
  async getTemplates(params = {}) {
    return this.get('/templates', params);
  }

  /**
   * 获取模板详情
   * @param {string} templateId - 模板 ID
   */
  async getTemplate(templateId) {
    return this.get(`/templates/${templateId}`);
  }

  // ============ 菜单管理 ============

  /**
   * 获取菜单
   * @param {string} menuKey - 菜单标识
   */
  async getMenu(menuKey) {
    return this.get(`/menus/${menuKey}`);
  }

  /**
   * 更新菜单（管理员）
   * @param {string} menuKey - 菜单标识
   * @param {Object} menuData - 菜单数据
   */
  async updateMenu(menuKey, menuData) {
    return this.put(`/menus/${menuKey}`, menuData);
  }

  // ============ AI 内容生成 (SIC 对接) ============

  /**
   * AI 生成内容
   * @param {Object} params - { topic, style?, platform? }
   * @returns {Promise<{title, content, source, model}>}
   */
  async aiGenerateContent(params) {
    return this.post('/ai/generate-content', params);
  }

  // ============ 内容排期 (SIC 对接) ============

  /**
   * 获取内容日历
   * @param {number} days - 天数 (默认 7)
   * @returns {Promise<{calendar: Array}>}
   */
  async getContentCalendar(days = 7) {
    return this.get('/content/calendar', { days });
  }

  /**
   * 获取最佳发布时间
   * @param {string} platform - 平台名称
   * @returns {Promise<{best_times: Array}>}
   */
  async getBestPublishTimes(platform) {
    return this.get(`/content/best-times/${platform}`);
  }

  /**
   * 获取内容统计
   * @returns {Promise<{total, published, scheduled, draft, by_status}>}
   */
  async getContentStats() {
    return this.get('/content/stats');
  }

  /**
   * 发布内容
   * @param {string} contentId - 内容 ID
   * @returns {Promise<{status}>}
   */
  async publishContent(contentId) {
    return this.post(`/content/${contentId}/publish`);
  }
}

// 创建默认实例
export const cmsApi = new CmsApiClient();



export default cmsApi;

export const CMSApiClient = {};
